import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Bookmark, BookmarkBorder, CalendarMonth } from '@mui/icons-material';
import {
  estimateBuyingPower,
  describeFeeComparisonShort,
} from '../../utils/buyerBrokerFee';
import {
  AGENT_FEE_FOOTNOTE,
  LD,
  NO_COMMITMENT_FOOTNOTE,
  formatCurrency,
} from './listingDialog/tokens';

interface ValueSummaryCardProps {
  propertyPrice: number;
  onTourClick: () => void;
  onSaveClick?: () => void;
  isSaved?: boolean;
  isSaving?: boolean;
  /** Overrides "Save home" / "Saved" (e.g. "Saved to your homes"). */
  saveLabel?: string;
  /** A server save cannot be undone from here; a device save can. */
  saveLocked?: boolean;
  tourButtonText?: string;
  tourButtonIcon?: React.ReactNode;
  tourButtonDisabled?: boolean;
}

/**
 * The desktop listing dialog's action card: the buying-power figure, the
 * tour button and Save. It sits beside the details and does not scroll on
 * its own.
 */
const ValueSummaryCard: React.FC<ValueSummaryCardProps> = ({
  propertyPrice,
  onTourClick,
  onSaveClick,
  isSaved = false,
  isSaving = false,
  saveLabel,
  saveLocked = false,
  tourButtonText,
  tourButtonIcon,
  tourButtonDisabled = false,
}) => {
  const estimate = estimateBuyingPower(propertyPrice);
  const hasBuyingPower = estimate.buyingPower > 0;
  let defaultTourText = 'Tour with Homepoint';
  if (hasBuyingPower) {
    defaultTourText = `See Home & Use ${formatCurrency(estimate.buyingPower)}`;
  }

  return (
    <Box data-testid="value-summary-card">
      {hasBuyingPower && (
        <Box
          sx={{
            bgcolor: LD.greenTint,
            border: `1px solid ${LD.greenLine}`,
            borderRadius: '12px',
            px: '14px',
            py: '12px',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 800,
              letterSpacing: '0.08em',
              color: LD.green,
              lineHeight: 'normal',
            }}
          >
            ESTIMATED BUYING POWER
          </Typography>
          <Typography
            data-testid="buying-power-amount"
            sx={{
              fontSize: '38px',
              fontWeight: 800,
              color: LD.green,
              lineHeight: 'normal',
            }}
          >
            {formatCurrency(estimate.buyingPower)}
          </Typography>
          <Typography
            sx={{ fontSize: '12.5px', color: LD.muted, lineHeight: 'normal' }}
          >
            {describeFeeComparisonShort(estimate)}
          </Typography>
        </Box>
      )}

      <Button
        variant="contained"
        fullWidth
        disableElevation
        startIcon={
          tourButtonIcon || (
            <CalendarMonth sx={{ fontSize: '20px !important' }} />
          )
        }
        onClick={onTourClick}
        disabled={tourButtonDisabled}
        sx={{
          mt: hasBuyingPower ? '12px' : 0,
          height: 50,
          borderRadius: '10px',
          fontSize: '16px',
          fontWeight: 700,
          textTransform: 'none',
          bgcolor: LD.primary,
          '&:hover': { bgcolor: LD.primaryDark },
        }}
      >
        {tourButtonText || defaultTourText}
      </Button>

      {onSaveClick && (
        <Button
          variant="outlined"
          fullWidth
          startIcon={isSaved ? <Bookmark /> : <BookmarkBorder />}
          onClick={onSaveClick}
          disabled={isSaving || (isSaved && saveLocked)}
          aria-pressed={isSaved}
          sx={{
            mt: '9px',
            height: 44,
            borderRadius: '10px',
            border: `1.5px solid ${isSaved ? LD.green : LD.saveBorder}`,
            color: isSaved ? LD.green : LD.ink2,
            fontSize: '15px',
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': {
              border: `1.5px solid ${isSaved ? LD.green : LD.muted2}`,
              bgcolor: 'white',
            },
            '&.Mui-disabled': {
              border: `1.5px solid ${isSaved ? LD.green : LD.saveBorder}`,
              color: isSaved ? LD.green : LD.muted2,
            },
          }}
        >
          {saveLabel || (isSaved ? 'Saved' : 'Save home')}
        </Button>
      )}

      <Typography
        sx={{
          fontSize: '12.5px',
          color: LD.muted,
          textAlign: 'center',
          mt: '8px',
          lineHeight: 1.4,
        }}
      >
        {hasBuyingPower ? AGENT_FEE_FOOTNOTE : NO_COMMITMENT_FOOTNOTE}
      </Typography>
    </Box>
  );
};

export default ValueSummaryCard;
